// @ts-nocheck
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { apiClient } from '../api/client';
import type {
  SystemSettings,
  GeneralSettings,
  SecuritySettings,
  KYCSettings,
  NotificationSettings,
  PerformanceSettings,
  SettingsAuditLogResponse,
} from '../api/client';

// Custom hook for system settings management
export const useSystemSettings = () => {
  const queryClient = useQueryClient();

  // Fetch all system settings
  const {
    data: settings,
    isLoading: loading,
    error,
    refetch,
  } = useQuery<SystemSettings>({
    queryKey: ['systemSettings'],
    queryFn: () => apiClient.getSystemSettings(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 3,
  });

  // Update general settings mutation
  const updateGeneralSettings = useMutation({
    mutationFn: (data: Partial<GeneralSettings>) =>
      apiClient.updateGeneralSettings(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['systemSettings'] });
    },
  });

  // Update security settings mutation
  const updateSecuritySettings = useMutation({
    mutationFn: (data: Partial<SecuritySettings>) =>
      apiClient.updateSecuritySettings(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['systemSettings'] });
    },
  });

  // Update KYC settings mutation
  const updateKYCSettings = useMutation({
    mutationFn: (data: Partial<KYCSettings>) =>
      apiClient.updateKYCSettings(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['systemSettings'] });
    },
  });

  // Update notification settings mutation
  const updateNotificationSettings = useMutation({
    mutationFn: (data: Partial<NotificationSettings>) =>
      apiClient.updateNotificationSettings(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['systemSettings'] });
    },
  });

  // Update performance settings mutation
  const updatePerformanceSettings = useMutation({
    mutationFn: (data: Partial<PerformanceSettings>) =>
      apiClient.updatePerformanceSettings(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['systemSettings'] });
    },
  });

  // Validate settings mutation
  const validateSettings = useMutation({
    mutationFn: ({ category, data }: { category: string; data: any }) => {
      // Use client-side validation instead of API call
      const {
        validateSystemSettings,
      } = require('../schemas/systemSettingsValidation');
      return validateSystemSettings({ [category]: data });
    },
  });

  // Reset settings mutation
  const resetSettings = useMutation({
    mutationFn: (category: string) => apiClient.resetSettings(category),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['systemSettings'] });
    },
  });

  // Export settings mutation
  const exportSettings = useMutation({
    mutationFn: (categories?: string[]) => apiClient.exportSettings(categories),
  });

  // Import settings mutation
  const importSettings = useMutation({
    mutationFn: ({
      settings,
      options,
    }: {
      settings: unknown;
      options?: unknown;
    }) => apiClient.importSettings(settings, options),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['systemSettings'] });
    },
  });

  // Test notification settings mutation
  const testNotificationSettings = useMutation({
    mutationFn: ({ type, settings }: { type: string; settings: unknown }) =>
      apiClient.testNotificationSettings(type, settings),
  });

  return {
    // Data
    settings,
    loading,
    error,

    // Actions
    refetch,
    updateGeneralSettings,
    updateSecuritySettings,
    updateKYCSettings,
    updateNotificationSettings,
    updatePerformanceSettings,
    validateSettings,
    resetSettings,
    exportSettings,
    importSettings,
    testNotificationSettings,

    // Mutation states
    isUpdating:
      updateGeneralSettings.isPending ||
      updateSecuritySettings.isPending ||
      updateKYCSettings.isPending ||
      updateNotificationSettings.isPending ||
      updatePerformanceSettings.isPending,
    isResetting: resetSettings.isPending,
    isExporting: exportSettings.isPending,
    isImporting: importSettings.isPending,
    isTesting: testNotificationSettings.isPending,
  };
};

// Custom hook for category-specific settings
export const useSettingsByCategory = (category: string) => {
  const queryClient = useQueryClient();

  const {
    data: categorySettings,
    isLoading: loading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['systemSettings', category],
    queryFn: () => apiClient.getSettingsByCategory(category),
    enabled: !!category,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 3,
  });

  // Update category settings mutation
  const updateSettings = useMutation({
    mutationFn: (data: any) => {
      switch (category) {
        case 'general':
          return apiClient.updateGeneralSettings(data);
        case 'security':
          return apiClient.updateSecuritySettings(data);
        case 'kyc':
          return apiClient.updateKYCSettings(data);
        case 'notifications':
          return apiClient.updateNotificationSettings(data);
        case 'performance':
          return apiClient.updatePerformanceSettings(data);
        default:
          throw new Error(`Unknown category: ${category}`);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['systemSettings'] });
      queryClient.invalidateQueries({ queryKey: ['systemSettings', category] });
    },
  });

  // Validate settings mutation
  const validateSettings = useMutation({
    mutationFn: (data: unknown) => apiClient.validateSettings(category, data),
  });

  // Reset settings mutation
  const resetSettings = useMutation({
    mutationFn: () => apiClient.resetSettings(category),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['systemSettings'] });
      queryClient.invalidateQueries({ queryKey: ['systemSettings', category] });
    },
  });

  return {
    // Data
    settings: categorySettings,
    loading,
    error,

    // Actions
    refetch,
    updateSettings,
    validateSettings,
    resetSettings,

    // Mutation states
    isUpdating: updateSettings.isPending,
    isValidating: validateSettings.isPending,
    isResetting: resetSettings.isPending,
  };
};

// Custom hook for settings audit log
export const useSettingsAuditLog = (
  filters: {
    category?: string;
    page?: number;
    limit?: number;
    start_date?: string;
    end_date?: string;
  } = {}
) => {
  const {
    data: auditLog,
    isLoading: loading,
    error,
    refetch,
  } = useQuery<SettingsAuditLogResponse>({
    queryKey: ['settingsAuditLog', filters],
    queryFn: () => apiClient.getSettingsAuditLog(filters),
    staleTime: 2 * 60 * 1000, // 2 minutes
    retry: 3,
  });

  return {
    auditLog,
    loading,
    error,
    refetch,
  };
};

// Custom hook for performance metrics
export const usePerformanceMetrics = () => {
  const {
    data: metrics,
    isLoading: loading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['performanceMetrics'],
    queryFn: () => apiClient.getPerformanceMetrics(),
    staleTime: 1 * 60 * 1000, // 1 minute
    retry: 3,
    refetchInterval: 30 * 1000, // Refresh every 30 seconds
  });

  return {
    metrics,
    loading,
    error,
    refetch,
  };
};

export default useSystemSettings;
